import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { ListChecks, ChevronRight, Loader2 } from "lucide-react";
import {
  addToCard,
  customerList,
  customerListId,
} from "../../../server/ShoppingList";
import { getCart } from "../../../server/CollectSite";
import showAlert from "../../../server/ShowAlert";
import { handleCartResponse } from "../shoppingList/UserShoppingList";

const CartShoppingListPicker = ({ close, onAdded }) => {
  const dispatch = useDispatch();
  const shoppingList = useSelector(
    (state) => state.shoppingList?.shoppingList ?? [],
  );
  const productsInCart = useSelector(
    (state) => state.productsInCart?.productsInCart ?? [],
  );
  const stationId = useSelector((state) => state.user?.stationId);

  const [activeList, setActiveList] = useState(null);
  const [listProducts, setListProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loadingList, setLoadingList] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    dispatch(customerList());
  }, [dispatch]);

  const openList = useCallback(
    async (list) => {
      if (!stationId) {
        showAlert({ msgTitle: "אין תחנה מוגדרת", msgType: "error" });
        return;
      }

      setActiveList(list);
      setLoadingList(true);
      setListProducts([]);
      setSelectedIds(new Set());

      try {
        let products = list.items;
        if (!products || products.length === 0) {
          const response = await customerListId(list.id, stationId);
          products = response.data?.items ?? [];
        }

        setListProducts(products);
        setSelectedIds(new Set(products.map((p) => p.productId)));
      } catch (e) {
        console.error(e);
        showAlert({ msgTitle: "שגיאה בטעינת מוצרים", msgType: "error" });
        setActiveList(null);
      } finally {
        setLoadingList(false);
      }
    },
    [stationId],
  );

  const toggleProduct = (productId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const toggleAll = (selectAll) => {
    if (selectAll) {
      setSelectedIds(new Set(listProducts.map((p) => p.productId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleAddToCart = async () => {
    if (!stationId) {
      showAlert({ msgTitle: "אין תחנה מוגדרת", msgType: "error" });
      return;
    }

    const products = listProducts.filter((p) => selectedIds.has(p.productId));

    if (products.length === 0) {
      showAlert({ msgTitle: "בחר לפחות מוצר אחד", msgType: "error" });
      return;
    }

    setAdding(true);
    try {
      const response = await addToCard(stationId, products, productsInCart);
      const reservedItems = response.data?.reservedItems || [];
      handleCartResponse(
        reservedItems,
        products,
        dispatch,
        stationId,
        showAlert,
        getCart,
        response.data?.allFailed,
      );
      onAdded?.();
      close();
    } catch (e) {
      console.error(e);
      showAlert({ msgTitle: "שגיאה בהוספה לעגלה", msgType: "error" });
    } finally {
      setAdding(false);
    }
  };

  const allSelected =
    listProducts.length > 0 && selectedIds.size === listProducts.length;

  return (
    <div className="cart-list-picker">
      <header className="cart-list-picker__header">
        <span className="cart-list-picker__header-icon" aria-hidden>
          <ListChecks size={20} strokeWidth={2} />
        </span>
        <div>
          <h2 className="cart-list-picker__title">
            {activeList ? activeList.name : "בחר מרשימת קניות"}
          </h2>
          <p className="cart-list-picker__subtitle">
            {activeList
              ? "סמן את המוצרים שברצונך להוסיף לעגלה"
              : "בחר רשימה כדי להתחיל לקנות במהירות"}
          </p>
        </div>
      </header>

      {activeList ? (
        <div className="cart-list-picker__body">
          <button
            type="button"
            className="cart-list-picker__back"
            onClick={() => {
              setActiveList(null);
              setListProducts([]);
              setSelectedIds(new Set());
            }}
          >
            <ChevronRight size={16} aria-hidden />
            חזרה לרשימות
          </button>

          {loadingList ? (
            <div className="cart-list-picker__loading" role="status">
              <Loader2 size={22} className="cart-list-picker__spinner" />
              <span>טוען מוצרים...</span>
            </div>
          ) : listProducts.length === 0 ? (
            <p className="cart-list-picker__empty">אין פריטים ברשימה זו</p>
          ) : (
            <>
              <div className="cart-list-picker__toolbar">
                <button
                  type="button"
                  className="cart-list-picker__toggle-all"
                  onClick={() => toggleAll(!allSelected)}
                >
                  {allSelected ? "בטל בחירה" : "בחר הכל"}
                </button>
                <span className="cart-list-picker__count">
                  {selectedIds.size} מתוך {listProducts.length}
                </span>
              </div>

              <ul className="cart-list-picker__products">
                {listProducts.map((item) => {
                  const checked = selectedIds.has(item.productId);
                  const name = item.product?.name || item.productId || "מוצר";
                  return (
                    <li key={item.productId}>
                      <label className="cart-list-picker__product">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProduct(item.productId)}
                        />
                        <span className="cart-list-picker__product-name">
                          {name}
                        </span>
                        <span className="cart-list-picker__product-qty">
                          ×{item.quantity}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              <button
                type="button"
                className="cart-list-picker__add-btn"
                onClick={handleAddToCart}
                disabled={adding || selectedIds.size === 0}
              >
                {adding ? "מוסיף לעגלה..." : "הוסף לעגלה"}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="cart-list-picker__body">
          {shoppingList.length === 0 ? (
            <div className="cart-list-picker__no-lists">
              <p>אין רשימות קניות שמורות.</p>
              <NavLink
                className="cart-list-picker__create-link"
                to="/shopping-list"
                onClick={close}
              >
                צור רשימת קניות חדשה
              </NavLink>
            </div>
          ) : (
            <ul className="cart-list-picker__lists">
              {shoppingList.map((list) => (
                <li key={list.id}>
                  <button
                    type="button"
                    className="cart-list-picker__list-btn"
                    onClick={() => openList(list)}
                  >
                    <span className="cart-list-picker__list-name">
                      {list.name}
                    </span>
                    <ChevronRight
                      size={18}
                      className="cart-list-picker__list-chevron"
                      aria-hidden
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CartShoppingListPicker;
